import { Box, Typography } from "@mui/material"
import MainSection from "./MainSection";
import AppButton from "./AppButton";
import { useRef } from "react";

const GaugeMeter = ({ value, color, trackColor, label, tag, tagBg }) => {
    const r = 52;
    const cx = 70;
    const cy = 68;
    const circumference = Math.PI * r;
    const progress = (value / 100) * circumference;

    return (
        <Box sx={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
            <svg viewBox="0 0 140 85" style={{ width: '100%', maxWidth: 148 }}>
                {/* Track */}
                <path
                    d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
                    fill="none"
                    stroke={trackColor}
                    strokeWidth="13"
                    strokeLinecap="round"
                />
                {/* Progress */}
                <path
                    d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
                    fill="none"
                    stroke={color}
                    strokeWidth="13"
                    strokeLinecap="round"
                    strokeDasharray={`${progress} ${circumference}`}
                />
                {/* Value */}
                <text x={cx} y={cy - 14} textAnchor="middle" fontSize="24" fontWeight="bold"
                    fill={color} fontFamily="Roboto, sans-serif">{value}</text>
                <text x={cx} y={cy + 4} textAnchor="middle" fontSize="10"
                    fill="#aaa" fontFamily="Roboto, sans-serif">out of 100</text>
            </svg>
            <Box sx={{ mt: -0.5 }}>
                <Box sx={{
                    display: 'inline-block', background: tagBg, color,
                    fontSize: '0.68rem', fontWeight: 700, px: 1.2, py: 0.25,
                    borderRadius: 1, mb: 0.5,
                }}>
                    {tag}
                </Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#555' }}>{label}</Typography>
            </Box>
        </Box>
    );
};

const HomePage = ({ isSignedIn, email, token, setSignInOpen }) => {
    const mainSectionRef = useRef(null);

    const handleStartAnalysis = () => {
        if (!isSignedIn) {
            setSignInOpen(true);
            return;
        }
        if (mainSectionRef.current) {
            mainSectionRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    return (
        <Box sx={{ paddingTop: { xs: '56px', sm: '64px' } }}>

            <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                background: '#fdf4e3', padding: { xs: '5rem 1rem', md: '8rem 1rem' },
            }}>
                <Typography sx={{ fontSize: { xs: '1.25rem', sm: '2rem' }, fontWeight: (theme) => theme.typography.fontWeightBold }}>
                    Match Your Resume To
                </Typography>
                <Typography sx={{ fontSize: { xs: '1.25rem', sm: '2rem' }, fontWeight: (theme) => theme.typography.fontWeightBold }}>
                    Job Descriptions
                </Typography>
                <Typography sx={{ margin: '1.5rem', maxWidth: 520, textAlign: 'justify', hyphens: 'auto', wordBreak: 'break-word' }}>
                    Upload your resume, paste a job description, and instantly see how well you match with scores, missing skills, and actionable insights powered by AI.
                </Typography>
                <AppButton onClick={handleStartAnalysis}>
                    Start Analysis
                </AppButton>

                {/* Odometer preview card */}
                <Box sx={{
                    mt: 5, width: '100%', maxWidth: 660,
                    background: 'white', borderRadius: 3,
                    boxShadow: '0 6px 32px rgba(0,0,0,0.10)',
                    p: { xs: 2.5, sm: 4 },
                }}>
                    <Typography sx={{
                        fontWeight: 700, fontSize: '0.7rem', letterSpacing: 1.8,
                        color: '#aaa', textTransform: 'uppercase', mb: 3, textAlign: 'center',
                    }}>
                        Sample Analysis Preview
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'flex-start', gap: 1 }}>
                        <GaugeMeter value={82} color="#4caf50" trackColor="#e8f5e9" label="ATS Score"        tag="Great" tagBg="#e8f5e9" />
                        <GaugeMeter value={61} color="#ff9800" trackColor="#fff3e0" label="Skill Match"      tag="Fair"  tagBg="#fff3e0" />
                        <GaugeMeter value={38} color="#f44336" trackColor="#fdecea" label="Keyword Coverage" tag="Low"   tagBg="#fdecea" />
                    </Box>

                    {/* Divider */}
                    <Box sx={{ borderTop: '1px solid #f0f0f0', mt: 3, mb: 2.5 }} />

                    {/* Skill chips */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {['React', 'Python', 'SQL', 'Node.js'].map(s => (
                            <Box key={s} sx={{
                                background: '#e8f5e9', color: '#2e7d32',
                                fontSize: '0.75rem', fontWeight: 600,
                                px: 1.4, py: 0.45, borderRadius: 10,
                            }}>
                                ✓ {s}
                            </Box>
                        ))}
                        {['Docker', 'AWS', 'GraphQL'].map(s => (
                            <Box key={s} sx={{
                                background: '#fdecea', color: '#c62828',
                                fontSize: '0.75rem', fontWeight: 600,
                                px: 1.4, py: 0.45, borderRadius: 10,
                            }}>
                                ✗ {s}
                            </Box>
                        ))}
                    </Box>

                    <Typography sx={{ fontSize: '0.7rem', color: '#ccc', mt: 2.5, textAlign: 'center' }}>
                        Sign in and upload your resume to see your real scores
                    </Typography>
                </Box>
            </Box>

            <Box ref={mainSectionRef}>
                <Box sx={{
                    filter: isSignedIn ? "none" : "blur(5px)",
                    pointerEvents: isSignedIn ? "auto" : "none",
                    transition: "filter 0.3s ease",
                }}>
                    <MainSection email={email} token={token} />
                </Box>
            </Box>
        </Box>
    );
};

export default HomePage;
